import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./horizonTable.css";
import "../forms/formTitle.css";
import "../forms/themeCheckbox.css";
import { addLogoAndFormHeader, applyFooterAllPages, getInnerTableWidth, buildSharePointFileName, toDdMmYyyy, addedByName } from "../../utils/formPdfHelpers.js";
import CheckTick from "../../assets/icons/Check-Tick.png";
import AdminTaylorImportModal, { adminIsPrimaryAdmin } from "./AdminTaylorImportModal";
import { ensureFormUuid } from "../../utils/formUuid";

import { canEditDeleteRecords } from "../../utils/rolePermissions";
const THEME = "#8b1634";

function getBaseUrl() {
  const apiUrl = process.env.REACT_APP_API_URL;
  return apiUrl?.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl || "";
}

const DEFAULT_FORM_DATA = {
  permitDetails: { permitNo: "", date: "", validFrom: "", validTo: "", location: "", responsiblePersonHseqAdvisor: "" },
  desktopSearch_rpHseq: { name: "", date: "" },
  siteReconnaissance_rpHseq: { name: "", date: "" },
  detectionSurvey_picex: { name: "", date: "" },
  verificationDetectionTrialHole_rpHseq: { name: "", date: "" },
  personInChargeOfExcavation: { name: "", date: "" },
  section1_questions: {
    "Type D Survey. Has  SF-027-A (Buried services summary sheet) and SF-027-B (Buried Service Survey Checklist) been completed Do you have service plans covering the area to be excavated, which are clear, of suitable scale, in colour where available, dated within the last 3 months and includes a symbol legend? Also refer to Section 7. Person / HSEQ Advisor sketch of proposed locations.": "",
    "Type C Survey. Has Buried Service Survey Checklist been undertaken ?": "",
    "Have you checked for visual signs of services e.g. marker posts, scarring,": "",
    "Have you received training on service avoidance and the use of CAT and Genny?": "",
    "Do you have a current NRSWA Operatives qualification for excavation?": "",
    "Are your tools and equipment in good working order?": "",
    "Is your CAT and Genny within certification/calibration date?": "",
    "Type (B) Survey Have you located all indicated underground services (including those outside of the area to be excavated) using the appropriate approved, certified CAT and Genny?": "",
    "Have you marked up all services on site (extended beyond excavation area) and taken a photo?": "",
    "Have you clipped onto a lamppost (where present) to identify streetlight cables?": "",
    "Have you checked for overhead services Is the work area more than 10m (horizontal distance) away from a high voltage overhead electricity cable?": "",
    "Can you carry out the work without the need to use mechanical or powered hand tools within 500mm of a known or indicated service?": "",
    "Can you confirm that the services are not likely to be embedded in concrete?": "",
    "Can you adopt safe digging practices?": "",
    "Are the works more than 10m away from any watercourse? (river, stream, pond, ditch, etc...)": "",
    "Are works more than 2m away from any tree trunk?": "",
    "Once located, can the services be adequately protected and supported?": "",
  },
  section2_questions: {
    "Is the excavation planned to be LESS THAN 1.2m deep?": "",
    "Have you completed an on-site POWA?": "",
    "Can you provide safe access/egress into the excavation?": "",
    "If working in poor ground, do you have equipment available to support the excavation? (only answer if ground is known to be poor)": "",
    "Do you have equipment available (e.g. barriers) to prevent people falling into the excavation?": "",
    "Can all materials be safely stored/stocked?": "",
  },
  catAndGenny: { catSerialNo: "", catCalibrationDate: "", gennySerialNo: "", gennyCalibrationDate: "" },
  servicesPresent: { electricity_lowVoltage: false, electricity_overhead: false, gas_lowPressure: false, water_mainLess300mmDia: false, service: false, other_sewerDrainage: false, other_communication: false },
  hazardousServices: { electricity_extraHighVoltage: false, electricity_highVoltageOverhead: false, gas_highPressure: false, gas_mediumIntermediatePressure: false, water_main300mmOrOver: false, water_oilOrFuel: false, water_grpOrUpvcMain: false, other_thrustBlocks: false },
  section3_issueAuthorisation: { signature: "", position: "", date: "" },
  section4_issueAuthorisation: { responsiblePersonMitigation: null, position: "", date: "" },
  typeASurvey: { width: "", depth: "", responsiblePersonName: "", signature: "" },
  section5_picexAcceptance: {
    "I understand the validity and conditions of authorisation placed on this permit by The Responsible Person / HSEQ Advisor.": false,
    "I agree to carry out the work in accordance with those conditions of authorisation and the associated Sae System Of Work.": false,
    "I have conducted any surveys required of the conditions of the permit to breaking ground.": false,
    "I have briefed all the members of the work group on the risk controls to be applied for conducting the work safely.": false,
    name: "", signature: "", time: "", date: "",
  },
  briefedTo: { name: "", signature: "", date: "", time: "" },
  section6_cancellation: { signature: "", position: "", date: "" },
  section7_sketch: { sketchImage: "", supervisorManagerName: "", signature: "", position: "", date: "" },
};

const SECTION1_QUESTIONS = Object.keys(DEFAULT_FORM_DATA.section1_questions);
const SECTION2_QUESTIONS = Object.keys(DEFAULT_FORM_DATA.section2_questions);

function underscoreKeyToCamelAlias(k) {
  return String(k || "")
    .split("_")
    .map((p, i) => (i ? p.charAt(0).toUpperCase() + p.slice(1) : p))
    .join("");
}

const PERMIT_SERVICES_PRESENT_KEYS = Object.keys(DEFAULT_FORM_DATA.servicesPresent);
const PERMIT_HAZARDOUS_SERVICES_KEYS = Object.keys(DEFAULT_FORM_DATA.hazardousServices);
const HAZARDOUS_EXTRA_SOURCE_KEYS = {
  electricity_extraHighVoltage: ["electricityExtraHighVoltage"],
  electricity_highVoltageOverhead: ["electricityHighVoltage", "electricityHighVoltageOverhead"],
  gas_highPressure: ["gasHighPressure"],
  gas_mediumIntermediatePressure: ["gasMediumIntermediatePressure"],
  water_main300mmOrOver: ["waterMain300mmOrOver"],
  water_oilOrFuel: ["otherOilOrFuel", "waterOilOrFuel"],
  water_grpOrUpvcMain: ["waterGRPOrUPVC", "waterGrpOrUpvcMain", "waterGrpOrUpvc"],
  other_thrustBlocks: ["otherThrustBlocks"],
};

const EXTRA_COND_LOOSE_KEYS = new Set([
  ...Object.values(HAZARDOUS_EXTRA_SOURCE_KEYS).flat(),
  ...Object.keys(HAZARDOUS_EXTRA_SOURCE_KEYS).map((k) => underscoreKeyToCamelAlias(k)),
]);

const PERMIT_LOOSE_SERVICE_KEYS = new Set([
  ...PERMIT_SERVICES_PRESENT_KEYS,
  ...PERMIT_HAZARDOUS_SERVICES_KEYS,
  ...PERMIT_SERVICES_PRESENT_KEYS.map((k) => underscoreKeyToCamelAlias(k)),
  ...PERMIT_HAZARDOUS_SERVICES_KEYS.map((k) => underscoreKeyToCamelAlias(k)),
  ...EXTRA_COND_LOOSE_KEYS,
]);
const tableCellStyle = { border: "1px solid #d1d5db", padding: "8px 12px", fontSize: 12, verticalAlign: "middle" };
const tableHeaderStyle = { ...tableCellStyle, fontWeight: 600, color: "#8b1634", background: "#fafafa" };

function parseMaybeJsonObject(val) {
  if (typeof val !== "string") return val;
  const raw = val.trim();
  if (!raw || !(raw.startsWith("{") || raw.startsWith("["))) return val;
  try {
    return JSON.parse(raw);
  } catch (_e) {
    return val;
  }
}

function coercePermitCheckboxBool(v) {
  if (v === true || v === false) return v;
  if (v === 1 || v === 0) return Boolean(v);
  const s = String(v == null ? "" : v)
    .trim()
    .toLowerCase();
  if (s === "yes" || s === "true" || s === "1") return true;
  if (s === "no" || s === "false" || s === "0" || s === "") return false;
  return Boolean(v);
}

function normalizePermitCheckboxMap(template, incoming, looseRoot) {
  let src = parseMaybeJsonObject(incoming);
  if (!src || typeof src !== "object" || Array.isArray(src)) src = {};
  const root = looseRoot && typeof looseRoot === "object" ? looseRoot : {};
  const out = { ...template };
  for (const key of Object.keys(template)) {
    const camel = underscoreKeyToCamelAlias(key);
    const v =
      src[key] !== undefined
        ? src[key]
        : src[camel] !== undefined
          ? src[camel]
          : root[key] !== undefined
            ? root[key]
            : root[camel] !== undefined
              ? root[camel]
              : undefined;
    if (v !== undefined) out[key] = coercePermitCheckboxBool(v);
  }
  return out;
}

function resolveHazardousFieldValue(templateKey, nested, conditional, looseRoot) {
  const camel = underscoreKeyToCamelAlias(templateKey);
  const extras = HAZARDOUS_EXTRA_SOURCE_KEYS[templateKey] || [];
  const buckets = [nested, conditional, looseRoot];
  for (const b of buckets) {
    if (!b || typeof b !== "object" || Array.isArray(b)) continue;
    if (b[templateKey] !== undefined) return b[templateKey];
    if (b[camel] !== undefined) return b[camel];
    for (const ex of extras) {
      if (b[ex] !== undefined) return b[ex];
    }
  }
  return undefined;
}

function normalizeHazardousServicesMap(template, incoming, looseRoot, conditionalRaw) {
  let nested = parseMaybeJsonObject(incoming);
  if (!nested || typeof nested !== "object" || Array.isArray(nested)) nested = {};
  const conditional = parseMaybeJsonObject(conditionalRaw);
  const root = looseRoot && typeof looseRoot === "object" ? looseRoot : {};
  const out = { ...template };
  for (const key of Object.keys(template)) {
    const v = resolveHazardousFieldValue(key, nested, conditional, root);
    if (v !== undefined) out[key] = coercePermitCheckboxBool(v);
  }
  return out;
}

function normalizeBriefedToMap(incoming, looseRoot) {
  let nested = parseMaybeJsonObject(incoming);
  if (!nested || typeof nested !== "object" || Array.isArray(nested)) nested = {};
  const root = looseRoot && typeof looseRoot === "object" ? looseRoot : {};
  const initialPermit = parseMaybeJsonObject(root.initialPermit);
  const sourceInitial = initialPermit && typeof initialPermit === "object" && !Array.isArray(initialPermit) ? initialPermit : {};
  return {
    name:
      nested.name ??
      nested.briefedToName ??
      root.initialPermitName ??
      sourceInitial.name ??
      sourceInitial.briefedToName ??
      "",
    signature:
      nested.signature ??
      nested.signed ??
      nested.sign ??
      root.initialPermitSignature ??
      sourceInitial.signature ??
      sourceInitial.signed ??
      sourceInitial.sign ??
      "",
    date:
      nested.date ??
      nested.briefedToDate ??
      root.initialPermitDate ??
      sourceInitial.date ??
      sourceInitial.briefedToDate ??
      "",
    time:
      nested.time ??
      nested.briefedToTime ??
      root.initialPermitTime ??
      sourceInitial.time ??
      sourceInitial.briefedToTime ??
      "",
  };
}

function stripLoosePermitServiceKeys(raw) {
  if (!raw || typeof raw !== "object") return {};
  const o = { ...raw };
  for (const k of PERMIT_LOOSE_SERVICE_KEYS) {
    if (Object.prototype.hasOwnProperty.call(o, k)) delete o[k];
  }
  return o;
}

function normalizePermitFormRawForEdit(raw) {
  if (!raw || typeof raw !== "object") return {};
  const base = stripLoosePermitServiceKeys(raw);
  return {
    ...base,
    servicesPresent: normalizePermitCheckboxMap(DEFAULT_FORM_DATA.servicesPresent, raw.servicesPresent, raw),
    hazardousServices: normalizeHazardousServicesMap(
      DEFAULT_FORM_DATA.hazardousServices,
      raw.hazardousServices,
      raw,
      raw.conditionalActionServices
    ),
    briefedTo: normalizeBriefedToMap(raw.briefedTo, raw),
  };
}

function mergePermitRecordIntoDefaults(raw) {
  return deepMerge(JSON.parse(JSON.stringify(DEFAULT_FORM_DATA)), normalizePermitFormRawForEdit(raw || {}));
}

function deepMerge(target, source) {
  const out = { ...target };
  for (const k of Object.keys(source || {})) {
    if (source[k] && typeof source[k] === "object" && !Array.isArray(source[k]) && source[k] !== null && !(source[k] instanceof Date) && typeof source[k].getMonth !== "function") {
      out[k] = deepMerge(target[k] || {}, source[k]);
    } else {
      out[k] = source[k];
    }
  }
  return out;
}

function getNestedFormVal(obj, path) {
  if (!obj || !path) return undefined;
  return path.split(".").reduce((o, k) => (o != null ? o[k] : undefined), obj);
}

function formatPdfScalar(v) {
  if (v === true) return "Yes";
  if (v === false) return "No";
  if (v == null || v === "") return "—";
  if (typeof v === "string" && v.startsWith("data:image")) return "(image)";
  return String(v);
}

function flattenFormDataRows(obj, prefix = "") {
  const rows = [];
  if (obj == null) return rows;
  if (typeof obj !== "object" || Array.isArray(obj)) {
    rows.push([prefix || "value", formatPdfScalar(obj)]);
    return rows;
  }
  for (const k of Object.keys(obj)) {
    const path = prefix ? `${prefix} › ${k}` : k;
    const v = obj[k];
    if (v !== null && typeof v === "object" && !Array.isArray(v) && !(v instanceof Date)) {
      rows.push(...flattenFormDataRows(v, path));
    } else {
      rows.push([path, formatPdfScalar(v)]);
    }
  }
  return rows;
}

function dateToLocalYMD(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function parseDateForPicker(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === "string") {
    const m = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) {
      const y = Number(m[1]);
      const mo = Number(m[2]) - 1;
      const day = Number(m[3]);
      const d = new Date(y, mo, day);
      return isNaN(d.getTime()) ? null : d;
    }
  }
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

function PermitToBreakGroundPage() {
  const canEditDelete = canEditDeleteRecords();
  const [forms, setForms] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(true);
  const [appUsers, setAppUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const isAdminUser = adminIsPrimaryAdmin();
  const [editForm, setEditForm] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [uploadingKey, setUploadingKey] = useState(null);
  const [showSharePointUploadModal, setShowSharePointUploadModal] = useState(false);
  const [sharePointUploadRecord, setSharePointUploadRecord] = useState(null);
  const [sharePointPhaseType, setSharePointPhaseType] = useState("phase");
  const [sharePointPhaseNumber, setSharePointPhaseNumber] = useState("15");
  const [sharePointNetwork, setSharePointNetwork] = useState("");
  const [pendingDirectUpload, setPendingDirectUpload] = useState(false);
  useEffect(() => {
    if (pendingDirectUpload && sharePointUploadRecord) {
      setPendingDirectUpload(false);
      // eslint-disable-next-line no-use-before-define
      handleSharePointUpload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDirectUpload, sharePointUploadRecord]);

  useEffect(() => { fetchForms(); fetchAppUsers(); }, []);

  const getRawFormData = (f) => {
    if (!f || typeof f !== "object") return {};
    if (f.formData && typeof f.formData === "object") return f.formData;
    const { formData, name, date, userId, createdBy, _id, __v, createdAt, updatedAt, ...rest } = f;
    return rest;
  };

  async function fetchForms() {
    setLoadingRecords(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/permit-to-break-ground", { headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      setForms(res.ok && data.success && Array.isArray(data.data) ? data.data : []);
    } catch { setForms([]); }
    finally { setLoadingRecords(false); }
  }

  async function fetchAppUsers() {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/app-users", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success && Array.isArray(data.data)) {
        setAppUsers(data.data);
      } else {
        setAppUsers([]);
      }
    } catch {
      setAppUsers([]);
    }
  }

  const filtered = forms.filter((f) => {
    const fd = getRawFormData(f);
    const fields = [f.name, fd.permitDetails?.permitNo, fd.permitDetails?.location, fd.personInChargeOfExcavation?.name].map((v) => (v || "").toLowerCase());
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return words.length === 0 || words.every((w) => fields.some((field) => field.includes(w)));
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice().slice((page - 1) * pageSize, page * pageSize);

  const updateFormData = (path, value) => {
    const [section, key] = path.split(".");
    setEditForm((p) => {
      const next = { ...p, formData: { ...p.formData } };
      if (section && key) {
        next.formData[section] = { ...(next.formData[section] || {}), [key]: value };
      } else {
        next.formData[path] = value;
      }
      next.name = next.formData?.name || next.formData?.personInChargeOfExcavation?.name || next.formData?.section5_picexAcceptance?.name || next.formData?.briefedTo?.name;
      next.date = next.formData?.date || next.formData?.permitDetails?.date || next.formData?.section5_picexAcceptance?.date || next.formData?.section3_issueAuthorisation?.date;
      return next;
    });
  };

  const handleEdit = (f) => {
    const raw = getRawFormData(f);
    setEditForm({
      ...f,
      formUuid: ensureFormUuid(f?.formUuid),
      formData: mergePermitRecordIntoDefaults(raw),
    });
    setShowEditModal(true);
  };

  const handleEditSave = async () => {
    if (!editForm?._id) return;
    try {
      const token = localStorage.getItem("token");
      const raw = editForm.formData || {};
      const res = await fetch(getBaseUrl() + "/api/permit-to-break-ground/" + editForm._id, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...raw, userId: editForm.userId, formUuid: ensureFormUuid(editForm.formUuid) }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await fetchForms();
        setShowEditModal(false);
        setEditForm(null);
        toast.success("Form updated!");
      } else {
        toast.error(data.message || "Failed to update");
      }
    } catch { toast.error("Failed to update"); }
  };

  const handleDelete = (id) => { setDeleteId(id); setShowDeleteModal(true); };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/permit-to-break-ground/" + deleteId, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
      const data = await res.json();
      if (res.ok && data.success) {
        setForms(forms.filter((f) => f._id !== deleteId));
        toast.success("Form deleted!");
      } else toast.error(data.message || "Failed to delete");
    } catch { toast.error("Failed to delete"); }
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const formatDate = (d) => {
    if (!d) return "—";
    const x = new Date(d);
    return isNaN(x.getTime()) ? "—" : x.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  const safeDate = (value) => parseDateForPicker(value);

  const renderYesNo = (label, value, onChange) => {
    const norm = typeof value === "string" ? value.trim().toLowerCase() : value;
    const isYes = norm === "yes" || value === true;
    const isNo = norm === "no" || value === false;
    return (
      <div key={label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
        <span style={{ fontSize: 12, flex: 1 }}>{label}</span>
        <div style={{ display: "flex", gap: 16 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 12 }}>
            <input type="radio" name={label} checked={isYes} onChange={() => onChange("yes")} style={{ accentColor: THEME }} /> YES
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 12 }}>
            <input type="radio" name={label} checked={isNo} onChange={() => onChange("no")} style={{ accentColor: THEME }} /> NO
          </label>
        </div>
      </div>
    );
  };

  const renderSignature = (sigKey, sigVal) => {
    const boxStyle = { width: "100%", maxWidth: 280, border: `2px solid ${THEME}`, borderRadius: 12, background: "rgba(139,22,52,0.06)", padding: 12, minHeight: 80, display: "flex", alignItems: "center", justifyContent: "center" };
    return (
      <div style={boxStyle}>
        {sigVal ? (
          <img src={sigVal.startsWith("data:image") ? sigVal : `data:image/png;base64,${sigVal}`} alt="Signature" style={{ maxWidth: "100%", maxHeight: 70, objectFit: "contain", borderRadius: 8, padding: 4, backgroundColor: "white", border: "1px solid #e2e8f0" }} onError={(e) => { e.target.style.display = "none"; }} />
        ) : (
          <div style={{ color: "#94a3b8", fontSize: 13, padding: 16 }}>No signature</div>
        )}
      </div>
    );
  };

  const renderSection = (title, children) => (
    <div style={{ marginBottom: 20, padding: 16, border: `1px solid #e2e8f0`, borderRadius: 12, background: "#f8fafc" }}>
      <div style={{ fontWeight: 700, color: THEME, marginBottom: 12, fontSize: 13 }}>{title}</div>
      {children}
    </div>
  );

  const fd = editForm?.formData || {};

  const getUserName = (record) => {
    const uid = record.userId;
    const u = appUsers.find((x) => x._id === uid);
    if (u) {
      const first = u.firstName || "";
      const last = u.lastName || "";
      const full = `${first} ${last}`.trim();
      return full || uid || "—";
    }
    return uid || "—";
  };

  const handleSharePointUpload = async () => {
    const fd0 = mergePermitRecordIntoDefaults(getRawFormData(sharePointUploadRecord));
    const pd = fd0.permitDetails || {};
    const locationForUpload = (pd.location || "").trim();
    if (!locationForUpload) {
      toast.error("Location is missing for this record.");
      return;
    }
    if (sharePointPhaseType === "phase" && (!sharePointNetwork || sharePointNetwork.trim() === "")) {
      toast.error("Please select an area (EPN, LPN, or SPN)");
      return;
    }
    const payloadPhaseType = sharePointPhaseType === "phase" ? "phase" : "non-phase";
    const payloadNetwork = sharePointPhaseType === "phase" ? sharePointNetwork : "";
    const payloadPhaseNumber = sharePointPhaseType === "phase" ? sharePointPhaseNumber : "";
    setShowSharePointUploadModal(false);
    await generatePDF(sharePointUploadRecord, {
      uploadOnly: true,
      phaseType: payloadPhaseType,
      phaseNumber: payloadPhaseNumber,
      network: payloadNetwork,
      siteName: locationForUpload,
      onSuccess: () => {
        setSharePointUploadRecord(null);
        setSharePointPhaseType("phase");
        setSharePointPhaseNumber("15");
        setSharePointNetwork("");
      },
      onError: () => {}
    });
  };

  const generatePDF = async (record, options = {}) => {
    const shouldUploadToSharePoint = options.uploadOnly === true;
    const uploadConfig = shouldUploadToSharePoint
      ? {
          phaseType: options.phaseType || "non-phase",
          phaseNumber: options.phaseNumber || "",
          network: options.network || "",
          siteName: options.siteName || "",
          onSuccess: options.onSuccess,
          onError: options.onError
        }
      : null;

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const innerTableW = getInnerTableWidth(doc);
    let yPosition = await addLogoAndFormHeader(doc, "SF-027-C Permit to Break Ground");

    const raw = getRawFormData(record);
    const fd = mergePermitRecordIntoDefaults(raw);
    const contentX = 10;
    const contentW = pageWidth - 20;
    const tableMargin = { left: 10, right: 10, bottom: 35 };
    const THEME_RGB = [139, 22, 52];
    const pendingSignaturePromises = [];

    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "normal");

    const yesX = (v) => (v === true || v === "true" || v === 1 || String(v).toLowerCase() === "yes" ? "X" : "");
    const noX = (v) => (v === false || v === "false" || v === 0 || String(v).toLowerCase() === "no" ? "X" : "");
    const isYesVal = (v) => v === true || v === "true" || v === 1 || String(v).toLowerCase() === "yes";
    const isNoVal = (v) => v === false || v === "false" || v === 0 || String(v).toLowerCase() === "no";
    const safePdfDate = (v) => (v ? formatDate(v) : "");

    const addSignatureToPDF = async (signatureData, x, y, maxWidth, maxHeight, pageNumber) => {
      if (!signatureData) return;
      let sigSrc = signatureData;
      if (!String(sigSrc).startsWith("data:image")) {
        sigSrc = `data:image/png;base64,${sigSrc}`;
      }

      return new Promise((resolve) => {
        const sigImg = new Image();
        sigImg.crossOrigin = "anonymous";
        sigImg.onload = () => {
          try {
            const aspectRatio = sigImg.width / sigImg.height;
            let imgWidth = maxWidth;
            let imgHeight = maxWidth / aspectRatio;
            if (imgHeight > maxHeight) {
              imgHeight = maxHeight;
              imgWidth = maxHeight * aspectRatio;
            }
            imgWidth = Math.min(imgWidth, maxWidth);
            imgHeight = Math.min(imgHeight, maxHeight);
            doc.setPage(pageNumber);
            doc.addImage(sigSrc, "PNG", x, y, imgWidth, imgHeight, "", "NONE");
          } catch {
            try {
              doc.setPage(pageNumber);
              doc.addImage(sigSrc, "PNG", x, y, maxWidth, maxHeight);
            } catch {}
          }
          resolve();
        };
        sigImg.onerror = () => resolve();
        sigImg.src = sigSrc;
      });
    };

    const addSectionHeaderBar = (title) => {
      if (yPosition + 10 > pageHeight - 35) {
        doc.addPage();
        yPosition = 20;
      }
      doc.setFillColor(...THEME_RGB);
      doc.rect(contentX, yPosition, contentW, 7, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7.2);
      doc.text(title, contentX + 2, yPosition + 4.8);
      yPosition += 9;
      doc.setTextColor(0, 0, 0);
      doc.setFont("helvetica", "normal");
    };

    const addThemeHeaderBarSingleLine = (title) => {
      if (yPosition + 10 > pageHeight - 35) {
        doc.addPage();
        yPosition = 20;
      }
      doc.setFillColor(...THEME_RGB);
      doc.rect(contentX, yPosition, contentW, 7, "F");
      doc.setTextColor(255, 255, 255);
      doc.setFont("helvetica", "bold");

      let fontSize = 7.2;
      const maxW = contentW - 4;
      doc.setFontSize(fontSize);
      try {
        while (fontSize > 3.6 && doc.getTextWidth(String(title)) > maxW) {
          fontSize -= 0.2;
          doc.setFontSize(fontSize);
        }
      } catch {
        // If width measuring isn't available, fallback to default size
      }

      doc.text(String(title), contentX + 2, yPosition + 4.8);
      yPosition += 9;
      doc.setTextColor(0, 0, 0);
      doc.setFont("helvetica", "normal");
    };

    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.setTextColor(156, 163, 175);
    doc.text("Permit to Break Ground", pageWidth / 2, yPosition + 8, { align: "center" });
    yPosition += 22;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    const intro =
      "This permit to break ground is to be completed prior to any invasive breaking/cutting or excavation below current surface level. Operations include but are not limited to excavating, driving stakes and fence posts, floor sawing and/or drilling.";
    const introLines = doc.splitTextToSize(intro, contentW);
    doc.text(introLines, contentX, yPosition);
    yPosition += introLines.length * 2.9 + 2;

    const permitDetails = fd.permitDetails || {};
    const permitRows = [
      ["Permit No:", permitDetails.permitNo || "", "Location:", permitDetails.location || ""],
      [
        "Responsible Person / HSEQ Advisor:",
        permitDetails.responsiblePersonHseqAdvisor || "",
        "Permit Date:",
        safePdfDate(record.date || permitDetails.date)
      ],
      ["Valid from (time):", permitDetails.validFrom || "", "Valid to (time):", permitDetails.validTo || ""]
    ];

    autoTable(doc, {
      startY: yPosition,
      head: [],
      body: permitRows,
      columnStyles: {
        0: { cellWidth: contentW * 0.25, fontStyle: "bold" },
        1: { cellWidth: contentW * 0.25 },
        2: { cellWidth: contentW * 0.25, fontStyle: "bold" },
        3: { cellWidth: contentW * 0.25 }
      },
      styles: { font: "helvetica", fontSize: 7, cellPadding: 1.2, overflow: "linebreak", valign: "top", fillColor: [255, 255, 255], textColor: [0, 0, 0] },
      theme: "grid",
      tableWidth: contentW,
      margin: tableMargin,
      headStyles: { fillColor: THEME_RGB, textColor: [255, 255, 255], lineWidth: 0.2 },
      bodyStyles: { lineWidth: 0.2, textColor: [0, 0, 0] }
    });
    yPosition = doc.lastAutoTable.finalY + 4;

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    const retained =
      "This form is to be completed by the Responsible Person / HSEQ Advisor (RP/HSEQ), for each job and retained in your team pack.";
    const retainedLines = doc.splitTextToSize(retained, contentW);
    doc.text(retainedLines, contentX, yPosition);
    yPosition += retainedLines.length * 3 + 3;

    const desktopRows = [
      ["(D) Desktop Search - (RP/HSEQ)", fd.desktopSearch_rpHseq?.name || "", safePdfDate(fd.desktopSearch_rpHseq?.date)],
      ["(C) Site Reconnaissance - (RP/HSEQ)", fd.siteReconnaissance_rpHseq?.name || "", safePdfDate(fd.siteReconnaissance_rpHseq?.date)],
      ["(B) Detection Survey - PICex", fd.detectionSurvey_picex?.name || "", safePdfDate(fd.detectionSurvey_picex?.date)],
      [
        "(A) Verification Detection / Trial Hole - (RP/HSEQ)",
        fd.verificationDetectionTrialHole_rpHseq?.name || "",
        safePdfDate(fd.verificationDetectionTrialHole_rpHseq?.date)
      ],
      [
        "Person in Charge of Excavation (PICex) / Person conducting buried service detection",
        fd.personInChargeOfExcavation?.name || "",
        safePdfDate(fd.personInChargeOfExcavation?.date)
      ]
    ];

    autoTable(doc, {
      startY: yPosition,
      head: [["", "Name", "Date"]],
      body: desktopRows,
      columnStyles: { 0: { cellWidth: contentW * 0.65 }, 1: { cellWidth: contentW * 0.2 }, 2: { cellWidth: contentW * 0.15 } },
      styles: { font: "helvetica", fontSize: 7, cellPadding: 1.2, valign: "middle", fillColor: [255, 255, 255], textColor: [0, 0, 0] },
      theme: "grid",
      tableWidth: contentW,
      margin: tableMargin,
      headStyles: { lineWidth: 0.2, fontStyle: "bold", fillColor: THEME_RGB, textColor: [255, 255, 255] },
      bodyStyles: { lineWidth: 0.2 }
    });
    yPosition = doc.lastAutoTable.finalY + 4;

    const section1Body = SECTION1_QUESTIONS.map((q) => [q, "", ""]);

    autoTable(doc, {
      startY: yPosition,
      head: [["Section 1 — Services", "YES", "NO"]],
      body: section1Body,
      columnStyles: { 0: { cellWidth: contentW * 0.7 }, 1: { cellWidth: contentW * 0.15, halign: "center" }, 2: { cellWidth: contentW * 0.15, halign: "center" } },
      styles: { font: "helvetica", fontSize: 6.4, cellPadding: 1.0, valign: "top", fillColor: [255, 255, 255], textColor: [0, 0, 0] },
      theme: "grid",
      tableWidth: contentW,
      margin: tableMargin,
      headStyles: { lineWidth: 0.2, fontStyle: "bold", fillColor: THEME_RGB, textColor: [255, 255, 255] },
      bodyStyles: { lineWidth: 0.2 },
      didDrawCell: (data) => {
        if (data.section !== "body") return;
        const q = SECTION1_QUESTIONS[data.row.index];
        const v = fd.section1_questions?.[q];
        if (data.column.index === 1 && isYesVal(v)) {
          const centerX = data.cell.x + data.cell.width / 2;
          const centerY = data.cell.y + data.cell.height / 2;
          doc.addImage(CheckTick, "PNG", centerX - 2, centerY - 2, 4, 4);
        }
        if (data.column.index === 2 && isNoVal(v)) {
          const centerX = data.cell.x + data.cell.width / 2;
          const centerY = data.cell.y + data.cell.height / 2;
          doc.addImage(CheckTick, "PNG", centerX - 2, centerY - 2, 4, 4);
        }
      }
    });
    yPosition = doc.lastAutoTable.finalY + 4;

    const BEIGE = THEME_RGB;
    const pdfDateOrSlash = (v) => {
      const d = safePdfDate(v);
      return d || "__/__/____";
    };

    const ensureSpace = (needed) => {
      if (yPosition + needed > pageHeight - 35) {
        doc.addPage();
        yPosition = 20;
      }
    };

    const catHeaderText =
      "CAT and Genny (Cable avoidance tools (CAT) and signal generator scanners must be approved for use on Network Rail Infrastructure. CAT4 and Genny4 units carry confirmed network rail approval).";
    addThemeHeaderBarSingleLine(catHeaderText);

    const cg = fd.catAndGenny || {};
    autoTable(doc, {
      startY: yPosition,
      head: [],
      body: [
        ["CAT Serial Number", cg.catSerialNo || "", "Calibration Due:", pdfDateOrSlash(cg.catCalibrationDate)],
        ["Genny Serial Number", cg.gennySerialNo || "", "Calibration Due:", pdfDateOrSlash(cg.gennyCalibrationDate)],
      ],
      columnStyles: {
        0: { cellWidth: contentW * 0.22, fontStyle: "bold" },
        1: { cellWidth: contentW * 0.28 },
        2: { cellWidth: contentW * 0.2, fontStyle: "bold" },
        3: { cellWidth: contentW * 0.3 },
      },
      styles: { font: "helvetica", fontSize: 6.5, cellPadding: 1.2, valign: "middle", fillColor: [255, 255, 255], textColor: [0, 0, 0], lineColor: [0, 0, 0], lineWidth: 0.2 },
      theme: "grid",
      tableWidth: contentW,
      margin: tableMargin,
    });
    yPosition = doc.lastAutoTable.finalY + 4;

    addThemeHeaderBarSingleLine("Services present");
    const spSub = "Services known or indicated within 1.5m of the excavation area or which may affect the work (tick if present)";
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.0);
    doc.setTextColor(0, 0, 0);
    const spSubLines = doc.splitTextToSize(spSub, contentW - 4);
    doc.text(spSubLines, contentX + 2, yPosition + 4);
    yPosition += spSubLines.length * 2.7 + 4;

    const sp = fd.servicesPresent || {};
    const col6 = contentW / 6;
    const drawTickSp = (data, field) => {
      if (data.section !== "body") return;
      const tickCols = [2, 5];
      if (!tickCols.includes(data.column.index)) return;
      const rowToField = [
        [null, null, "electricity_lowVoltage", null, null, "gas_lowPressure"],
        [null, null, "electricity_overhead", null, null, null],
        [null, null, "water_mainLess300mmDia", null, null, "other_sewerDrainage"],
        [null, null, "service", null, null, "other_communication"],
      ];
      const f = rowToField[data.row.index]?.[data.column.index];
      if (!f) return;
      const v = sp[f];
      if (isYesVal(v) || v === true) {
        const centerX = data.cell.x + data.cell.width / 2;
        const centerY = data.cell.y + data.cell.height / 2;
        doc.addImage(CheckTick, "PNG", centerX - 2, centerY - 2, 4, 4);
      }
    };

    const servicesGrid = [
      ["Electricity", "Low Voltage", "", "Gas", "Low Pressure", ""],
      ["", "Overhead", "", "", "", ""],
      ["Water", "Main less than 300mm dia.", "", "Other", "Sewer/drainage", ""],
      ["", "Service", "", "", "Communication", ""],
    ];

    autoTable(doc, {
      startY: yPosition,
      head: [],
      body: servicesGrid,
      columnStyles: {
        0: { cellWidth: col6 },
        1: { cellWidth: col6 },
        2: { cellWidth: col6, halign: "center" },
        3: { cellWidth: col6 },
        4: { cellWidth: col6 },
        5: { cellWidth: col6, halign: "center" },
      },
      styles: { font: "helvetica", fontSize: 6.2, cellPadding: 1.0, valign: "middle", fillColor: [255, 255, 255], textColor: [0, 0, 0], lineColor: [0, 0, 0], lineWidth: 0.2 },
      theme: "grid",
      tableWidth: contentW,
      margin: tableMargin,
      didDrawCell: drawTickSp,
    });
    yPosition = doc.lastAutoTable.finalY + 4;

    const hazHeaderText =
      "If any of the following services are indicated as present, DO NOT commence work until Section 4 of this permit has been completed by the Responsible Person / HSEQ Advisor.";
    addThemeHeaderBarSingleLine(hazHeaderText);

    const hs = fd.hazardousServices || {};
    const drawTickHs = (data) => {
      if (data.section !== "body") return;
      const rowToField = [
        [null, null, "electricity_extraHighVoltage", null, null, "gas_highPressure"],
        [null, null, "electricity_highVoltageOverhead", null, null, "gas_mediumIntermediatePressure"],
        [null, null, "water_main300mmOrOver", null, null, "water_oilOrFuel"],
        [null, null, "water_grpOrUpvcMain", null, null, "other_thrustBlocks"],
      ];
      const tickCols = [2, 5];
      if (!tickCols.includes(data.column.index)) return;
      const f = rowToField[data.row.index]?.[data.column.index];
      if (!f) return;
      const v = hs[f];
      if (isYesVal(v) || v === true) {
        const centerX = data.cell.x + data.cell.width / 2;
        const centerY = data.cell.y + data.cell.height / 2;
        doc.addImage(CheckTick, "PNG", centerX - 2, centerY - 2, 4, 4);
      }
    };

    const hazardousGrid = [
      ["Electricity", "Extra high voltage", "", "Gas", "High Pressure", ""],
      ["", "High voltage (inc. overhead)", "", "", "Medium/intermediate pressure", ""],
      ["Water", "Main (300mm dia. or over)", "", "Other", "Oil or Fuel", ""],
      ["", "GRP or UPVC main (requires isolation before excavating)", "", "", "Thrust blocks", ""],
    ];

    autoTable(doc, {
      startY: yPosition,
      head: [],
      body: hazardousGrid,
      columnStyles: {
        0: { cellWidth: col6 },
        1: { cellWidth: col6 },
        2: { cellWidth: col6, halign: "center" },
        3: { cellWidth: col6 },
        4: { cellWidth: col6 },
        5: { cellWidth: col6, halign: "center" },
      },
      styles: { font: "helvetica", fontSize: 6.0, cellPadding: 1.0, valign: "middle", fillColor: [255, 255, 255], textColor: [0, 0, 0], lineColor: [0, 0, 0], lineWidth: 0.2 },
      theme: "grid",
      tableWidth: contentW,
      margin: tableMargin,
      didDrawCell: drawTickHs,
    });
    yPosition = doc.lastAutoTable.finalY + 4;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.5);
    const hold2Header = "HOLD: If you answer NO to any of the following questions, WORK MUST NOT";
    doc.text(hold2Header, contentX, yPosition);
    yPosition += 3.2;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.3);
    const hold2Body = "PROCEED. Escalate to Responsible Person / HSEQ Advisor so that they can identify and implement additional control measures.";
    const hold2Lines = doc.splitTextToSize(hold2Body, contentW);
    doc.text(hold2Lines, contentX, yPosition);
    yPosition += hold2Lines.length * 2.8 + 2;

    const section2Body = SECTION2_QUESTIONS.map((q) => [q, "", ""]);

    autoTable(doc, {
      startY: yPosition,
      head: [["Section 2 — Excavations", "YES", "NO"]],
      body: section2Body,
      columnStyles: { 0: { cellWidth: contentW * 0.7 }, 1: { cellWidth: contentW * 0.15, halign: "center" }, 2: { cellWidth: contentW * 0.15, halign: "center" } },
      styles: { font: "helvetica", fontSize: 6.4, cellPadding: 1.0, valign: "top", fillColor: [255, 255, 255], textColor: [0, 0, 0] },
      theme: "grid",
      tableWidth: contentW,
      margin: tableMargin,
      headStyles: { lineWidth: 0.2, fontStyle: "bold", fillColor: THEME_RGB, textColor: [255, 255, 255] },
      bodyStyles: { lineWidth: 0.2, textColor: [0, 0, 0] },
      didDrawCell: (data) => {
        if (data.section !== "body") return;
        const q = SECTION2_QUESTIONS[data.row.index];
        const v = fd.section2_questions?.[q];
        if (data.column.index === 1 && isYesVal(v)) {
          const centerX = data.cell.x + data.cell.width / 2;
          const centerY = data.cell.y + data.cell.height / 2;
          doc.addImage(CheckTick, "PNG", centerX - 2, centerY - 2, 4, 4);
        }
        if (data.column.index === 2 && isNoVal(v)) {
          const centerX = data.cell.x + data.cell.width / 2;
          const centerY = data.cell.y + data.cell.height / 2;
          doc.addImage(CheckTick, "PNG", centerX - 2, centerY - 2, 4, 4);
        }
      }
    });
    yPosition = doc.lastAutoTable.finalY + 4;

    const BEIGE_HEADER = THEME_RGB;
    const sig3 = fd.section3_issueAuthorisation?.signature;
    const sig3Src =
      sig3 && typeof sig3 === "string"
        ? sig3.startsWith("data:")
          ? sig3
          : `data:image/png;base64,${sig3}`
        : null;

    const section3Title =
      "Section 3 — Issue/authorisation (to be completed by the Responsible Person / HSEQ Advisor where all answers are ‘YES’)";
    const section3BodyText =
      "I confirm that work is safe to proceed in accordance with the risk assessments and controls identified above";

    if (yPosition + 140 > pageHeight - 35) {
      doc.addPage();
      yPosition = 20;
    }

    const pos3 = fd.section3_issueAuthorisation?.position || "";
    const date3 = safePdfDate(fd.section3_issueAuthorisation?.date);

    autoTable(doc, {
      startY: yPosition,
      columnStyles: {
        0: { cellWidth: contentW * 0.33 },
        1: { cellWidth: contentW * 0.37 },
        2: { cellWidth: contentW * 0.3 }
      },
      styles: {
        font: "helvetica",
        fontSize: 7.0,
        cellPadding: 1.0,
        valign: "top",
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        lineColor: [0, 0, 0],
        lineWidth: 0.2
      },
      theme: "grid",
      tableWidth: contentW,
      margin: tableMargin,
      body: [
        [
          { content: section3Title, colSpan: 3, styles: { fillColor: BEIGE_HEADER, textColor: [255, 255, 255], fontStyle: "bold", fontSize: 7.3, halign: "left" } }
        ],
        [
          { content: section3BodyText, colSpan: 3, styles: { fillColor: [255, 255, 255], fontStyle: "normal", fontSize: 6.6, halign: "left" } }
        ],
        [
          { content: "Signed:", styles: { fontStyle: "normal" } },
          { content: `Position:\n${pos3}` },
          { content: `Date:\n${date3}` }
        ]
      ],
      bodyStyles: { lineWidth: 0.2 },
      didParseCell: (data) => {
        if (data.section !== "body") return;
        if (data.row.index === 2) {
          data.cell.styles.minCellHeight = 26;
        }
      },
      didDrawCell: (data) => {
        if (data.section !== "body") return;
        if (data.row.index === 2 && data.column.index === 0 && sig3Src) {
          const pageNumber = doc.internal.getCurrentPageInfo().pageNumber;
          pendingSignaturePromises.push(addSignatureToPDF(sig3Src, data.cell.x + 2, data.cell.y + 10, 22, 12, pageNumber));
        }
      }
    });
    yPosition = doc.lastAutoTable.finalY + 4;

    const mitigationVal = fd.section4_issueAuthorisation?.responsiblePersonMitigation;
    const section4Title =
      "Section 4 — Issue/authorisation (to be completed by the Responsible Person / HSEQ Advisor where one or more questions are answered ‘NO’)";
    const section4BodyText =
      "The following action has been taken/control measures put in place to ensure that work can proceed safely";

    autoTable(doc, {
      startY: yPosition,
      columnStyles: {
        0: { cellWidth: contentW * 0.7 },
        1: { cellWidth: contentW * 0.15, halign: "center" },
        2: { cellWidth: contentW * 0.15, halign: "center" }
      },
      styles: {
        font: "helvetica",
        fontSize: 6.3,
        cellPadding: 1.1,
        valign: "middle",
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        lineColor: [0, 0, 0],
        lineWidth: 0.2
      },
      theme: "grid",
      tableWidth: contentW,
      margin: tableMargin,
      body: [
        [
          { content: section4Title, colSpan: 3, styles: { fillColor: BEIGE_HEADER, textColor: [255, 255, 255], fontStyle: "bold", fontSize: 7.0, halign: "left" } }
        ],
        [
          { content: section4BodyText, colSpan: 3, styles: { fillColor: [255, 255, 255], fontStyle: "normal", fontSize: 6.2, halign: "left" } }
        ],
        [
          "Responsible Person / HSEQ Advisor mitigation",
          "Yes",
          "No"
        ],
        [
          { content: "Is Type (A) survey required?", styles: { textColor: [43, 128, 61], fontStyle: "bold" } },
          "",
          ""
        ]
      ],
      didParseCell: (data) => {
        if (data.section !== "body") return;
        if (data.row.index === 3 && (data.column.index === 1 || data.column.index === 2)) {
          data.cell.styles.minCellHeight = 18;
        }
      },
      didDrawCell: (data) => {
        if (data.section !== "body") return;
        if (data.row.index === 3) {
          if (data.column.index === 1 && isYesVal(mitigationVal)) {
            const centerX = data.cell.x + data.cell.width / 2;
            const centerY = data.cell.y + data.cell.height / 2;
            doc.addImage(CheckTick, "PNG", centerX - 2, centerY - 2, 4, 4);
          }
          if (data.column.index === 2 && isNoVal(mitigationVal)) {
            const centerX = data.cell.x + data.cell.width / 2;
            const centerY = data.cell.y + data.cell.height / 2;
            doc.addImage(CheckTick, "PNG", centerX - 2, centerY - 2, 4, 4);
          }
        }
      }
    });
    yPosition = doc.lastAutoTable.finalY + 4;

    const typeA = fd.typeASurvey || {};
    const typeASig = typeA.signature && typeof typeA.signature === "string" ? (typeA.signature.startsWith("data:") ? typeA.signature : `data:image/png;base64,${typeA.signature}`) : null;
    const typeADateFallback = fd.section4_issueAuthorisation?.date || fd.section3_issueAuthorisation?.date || "";
    doc.setFont("helvetica", "bold");
    doc.setFontSize(7.2);
    const typeATitle = "Type (A) Survey Verification Detection has been undertaken. This requires a trial hole/slit to be excavated to a width of";
    const typeATitleLines = doc.splitTextToSize(typeATitle, contentW);
    doc.text(typeATitleLines, contentX, yPosition);
    yPosition += typeATitleLines.length * 3.0 + 1;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(6.2);
    const typeAWidthDepth = `width of: ${typeA.width || ""}  /  depth: ${typeA.depth || ""}`;
    const tdLines = doc.splitTextToSize(typeAWidthDepth, contentW);
    doc.text(tdLines, contentX, yPosition);
    yPosition += tdLines.length * 3.0 + 1;

    const typeAPerson = typeA.responsiblePersonName || "";
    autoTable(doc, {
      startY: yPosition,
      head: [["Position:", "Signed", "Date", "Time"]],
      body: [[
        typeAPerson,
        { content: " ", styles: { minCellHeight: 20, fillColor: [255, 255, 255], textColor: [0, 0, 0] } },
        safePdfDate(typeADateFallback),
        ""
      ]],
      columnStyles: {
        0: { cellWidth: contentW * 0.35 },
        1: { cellWidth: contentW * 0.25, halign: "center" },
        2: { cellWidth: contentW * 0.2 },
        3: { cellWidth: contentW * 0.2 }
      },
      styles: { font: "helvetica", fontSize: 6.4, cellPadding: 1.0, valign: "middle", fillColor: [255, 255, 255], textColor: [0, 0, 0] },
      theme: "grid",
      tableWidth: contentW,
      margin: tableMargin,
      headStyles: { lineWidth: 0.2, fontStyle: "bold", fillColor: THEME_RGB, textColor: [255, 255, 255] },
      bodyStyles: { lineWidth: 0.2, textColor: [0, 0, 0] },
      didDrawCell: (data) => {
        if (data.section === "body" && data.column.index === 1 && typeASig) {
          const pageNumber = doc.internal.getCurrentPageInfo().pageNumber;
          pendingSignaturePromises.push(addSignatureToPDF(typeASig, data.cell.x + 3, data.cell.y + 3, 22, 12, pageNumber));
        }
      }
    });
    yPosition = doc.lastAutoTable.finalY + 4;

    const BEIGE_BOX = THEME_RGB;
    const ensureSpaceS56 = (h) => {
      if (yPosition + h > pageHeight - 35) {
        doc.addPage();
        yPosition = 20;
      }
    };

    const drawTitleBox = (titleText, extraMinHeight = 10) => {
      const lines = doc.splitTextToSize(titleText, contentW - 4);
      const boxH = Math.max(extraMinHeight, lines.length * 1.6 + 2);
      doc.setFillColor(...BEIGE_BOX);
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.2);
      doc.rect(contentX, yPosition, contentW, boxH, "FD");
      doc.rect(contentX, yPosition, contentW, boxH, "S");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(6.0);
      doc.setTextColor(255, 255, 255);
      doc.text(lines, contentX + 2, yPosition + 2.6);
      yPosition += boxH;
    };

    const drawParagraphBox = (text, fontSize = 6.2, minHeight = 14) => {
      const lines = doc.splitTextToSize(text, contentW - 4);
      const boxH = Math.max(minHeight, lines.length * 2.6 + 6);
      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.2);
      doc.rect(contentX, yPosition, contentW, boxH, "S");
      doc.setFont("helvetica", "normal");
      doc.setFontSize(fontSize);
      doc.setTextColor(0, 0, 0);
      doc.text(lines, contentX + 2, yPosition + 4);
      yPosition += boxH;
    };

    const s5 = fd.section5_picexAcceptance || {};
    const s5Statements = [
      "I understand the validity and conditions of authorisation placed on this permit by The Responsible Person / HSEQ Advisor.",
      "I agree to carry out the work in accordance with those conditions of authorisation and the associated Sae System Of Work.",
      "I have conducted any surveys required of the conditions of the permit to breaking ground.",
      "I have briefed all the members of the work group on the risk controls to be applied for conducting the work safely.",
    ];
    const sig5 = s5.signature && typeof s5.signature === "string" ? (s5.signature.startsWith("data:") ? s5.signature : `data:image/png;base64,${s5.signature}`) : null;

    ensureSpaceS56(10);
    addThemeHeaderBarSingleLine('Section 5 — PICex Acceptance "Permit to Break Ground"');

    const section5Rows = s5Statements.map((stmt) => [stmt, ""]);
    const s5SignHeadCell = { fillColor: THEME_RGB, fontStyle: "bold", textColor: [255, 255, 255] };

    autoTable(doc, {
      startY: yPosition,
      body: section5Rows,
      columnStyles: {
        0: { cellWidth: contentW * 0.9 },
        1: { cellWidth: contentW * 0.1, halign: "center" }
      },
      styles: { font: "helvetica", fontSize: 6.2, cellPadding: 1.0, valign: "top", fillColor: [255, 255, 255], textColor: [0, 0, 0] },
      theme: "grid",
      tableWidth: contentW,
      margin: tableMargin,
      bodyStyles: { lineWidth: 0.2, textColor: [0, 0, 0] },
      didDrawCell: (data) => {
        if (data.section !== "body" || data.column.index !== 1) return;
        const stmt = s5Statements[data.row.index];
        const v = s5[stmt];
        if (v === true || isYesVal(v)) {
          const centerX = data.cell.x + data.cell.width / 2;
          const centerY = data.cell.y + data.cell.height / 2;
          doc.addImage(CheckTick, "PNG", centerX - 2, centerY - 2, 4, 4);
        }
      }
    });
    yPosition = doc.lastAutoTable.finalY;

    autoTable(doc, {
      startY: yPosition,
      body: [
        [
          { content: "Name:", styles: s5SignHeadCell },
          { content: "Signed:", styles: s5SignHeadCell },
          { content: "Date:", styles: s5SignHeadCell },
          { content: "Time:", styles: s5SignHeadCell },
        ],
        [
          s5.name || "",
          { content: " ", styles: { minCellHeight: 20, fillColor: [255, 255, 255], textColor: [0, 0, 0] } },
          safePdfDate(s5.date),
          s5.time || ""
        ]
      ],
      columnStyles: {
        0: { cellWidth: contentW * 0.32 },
        1: { cellWidth: contentW * 0.25, halign: "center" },
        2: { cellWidth: contentW * 0.2 },
        3: { cellWidth: contentW * 0.23 }
      },
      styles: { font: "helvetica", fontSize: 6.2, cellPadding: 1.0, valign: "middle", fillColor: [255, 255, 255], textColor: [0, 0, 0] },
      theme: "grid",
      tableWidth: contentW,
      margin: tableMargin,
      bodyStyles: { lineWidth: 0.2, textColor: [0, 0, 0] },
      didDrawCell: (data) => {
        if (data.section === "body" && data.row.index === 1 && data.column.index === 1 && sig5) {
          const pageNumber = doc.internal.getCurrentPageInfo().pageNumber;
          pendingSignaturePromises.push(addSignatureToPDF(sig5, data.cell.x + 3, data.cell.y + 3, 22, 12, pageNumber));
        }
      }
    });
    yPosition = doc.lastAutoTable.finalY + 2;

    const b = fd.briefedTo || {};
    const briefSig = b.signature && typeof b.signature === "string" ? (b.signature.startsWith("data:") ? b.signature : `data:image/png;base64,${b.signature}`) : null;
    const briefSentence = "By signing below I confirm I have received and understood the briefing for this task.";
    const briefSignHeadCell = { fillColor: THEME_RGB, fontStyle: "bold", textColor: [255, 255, 255] };

    ensureSpaceS56(50);
    addThemeHeaderBarSingleLine("Briefed to");

    autoTable(doc, {
      startY: yPosition,
      body: [
        [
          {
            content: briefSentence,
            colSpan: 4,
            styles: { halign: "left", fontStyle: "normal", fillColor: [255, 255, 255], textColor: [0, 0, 0] },
          },
        ],
        [
          { content: "Name:", styles: briefSignHeadCell },
          { content: "Signed:", styles: briefSignHeadCell },
          { content: "Date:", styles: briefSignHeadCell },
          { content: "Time:", styles: briefSignHeadCell },
        ],
        [
          b.name || "",
          { content: " ", styles: { minCellHeight: 20, fillColor: [255, 255, 255], textColor: [0, 0, 0] } },
          safePdfDate(b.date),
          b.time || "",
        ],
      ],
      columnStyles: {
        0: { cellWidth: contentW * 0.32 },
        1: { cellWidth: contentW * 0.25, halign: "center" },
        2: { cellWidth: contentW * 0.2 },
        3: { cellWidth: contentW * 0.23 },
      },
      styles: { font: "helvetica", fontSize: 6.2, cellPadding: 1.0, valign: "middle", fillColor: [255, 255, 255], textColor: [0, 0, 0] },
      theme: "grid",
      tableWidth: contentW,
      margin: tableMargin,
      bodyStyles: { lineWidth: 0.2, textColor: [0, 0, 0] },
      didDrawCell: (data) => {
        if (data.section === "body" && data.row.index === 2 && data.column.index === 1 && briefSig) {
          const pageNumber = doc.internal.getCurrentPageInfo().pageNumber;
          pendingSignaturePromises.push(addSignatureToPDF(briefSig, data.cell.x + 3, data.cell.y + 3, 22, 12, pageNumber));
        }
      },
    });
    yPosition = doc.lastAutoTable.finalY + 2;

    const s6 = fd.section6_cancellation || {};
    const sig6 = s6.signature && typeof s6.signature === "string" ? (s6.signature.startsWith("data:") ? s6.signature : `data:image/png;base64,${s6.signature}`) : null;
    const s6ConfirmText = "I confirm that work has been completed / not completed and a new permit has been issued (delete as appropriate)";
    const s6SignHeadCell = { fillColor: THEME_RGB, fontStyle: "bold", textColor: [255, 255, 255] };

    ensureSpaceS56(70);
    addThemeHeaderBarSingleLine("Section 6 — Cancellation (to be completed by the Responsible Person / HSEQ Advisor)");

    autoTable(doc, {
      startY: yPosition,
      body: [
        [
          {
            content: s6ConfirmText,
            colSpan: 3,
            styles: { halign: "left", fontStyle: "normal", fillColor: [255, 255, 255], textColor: [0, 0, 0] },
          },
        ],
        [
          { content: "Signed:", styles: s6SignHeadCell },
          { content: "Position:", styles: s6SignHeadCell },
          { content: "Date:", styles: s6SignHeadCell },
        ],
        [
          { content: " ", styles: { minCellHeight: 20, fillColor: [255, 255, 255], textColor: [0, 0, 0] } },
          s6.position || "",
          safePdfDate(s6.date || ""),
        ],
      ],
      columnStyles: {
        0: { cellWidth: contentW * 0.25, halign: "center" },
        1: { cellWidth: contentW * 0.5 },
        2: { cellWidth: contentW * 0.25 },
      },
      styles: { font: "helvetica", fontSize: 6.2, cellPadding: 1.0, valign: "middle", fillColor: [255, 255, 255], textColor: [0, 0, 0] },
      theme: "grid",
      tableWidth: contentW,
      margin: tableMargin,
      bodyStyles: { lineWidth: 0.2, textColor: [0, 0, 0] },
      didDrawCell: (data) => {
        if (data.section === "body" && data.row.index === 2 && data.column.index === 0 && sig6) {
          const pageNumber = doc.internal.getCurrentPageInfo().pageNumber;
          pendingSignaturePromises.push(addSignatureToPDF(sig6, data.cell.x + 3, data.cell.y + 3, 22, 12, pageNumber));
        }
      },
    });
    yPosition = doc.lastAutoTable.finalY + 4;

    const sketch = getNestedFormVal(fd, "section7_sketch.sketchImage");
    const sketchPlaceholderH = 45;
    if (yPosition + sketchPlaceholderH > pageHeight - 35) {
      doc.addPage();
      yPosition = 20;
    }

    addSectionHeaderBar("Section 7 — Responsible Person / HSEQ Advisor sketch of proposed location");
    doc.rect(contentX, yPosition, contentW, sketchPlaceholderH);
    if (sketch && typeof sketch === "string") {
      const sketchSrc = sketch.startsWith("data:image")
        ? sketch
        : sketch.startsWith("http")
          ? ""
          : `data:image/png;base64,${sketch}`;
      if (sketchSrc) {
        try {
          const imgFormat = sketchSrc.startsWith("data:image/jpeg") || sketchSrc.startsWith("data:image/jpg") ? "JPEG" : "PNG";
          const pad = 2;
          const boxW = contentW - pad * 2;
          const boxH = sketchPlaceholderH - pad * 2;
          const props = doc.getImageProperties(sketchSrc);
          const iw = props.width || 1;
          const ih = props.height || 1;
          const aspect = iw / ih;
          let drawW = boxW;
          let drawH = boxW / aspect;
          if (drawH > boxH) {
            drawH = boxH;
            drawW = boxH * aspect;
          }
          const drawX = contentX + pad + (boxW - drawW) / 2;
          const drawY = yPosition + pad + (boxH - drawH) / 2;
          doc.addImage(sketchSrc, imgFormat, drawX, drawY, drawW, drawH, undefined, "NONE");
        } catch {}
      }
    }
    yPosition += sketchPlaceholderH + 2;

    const s7 = fd.section7_sketch || {};
    const sig7 = s7.signature && typeof s7.signature === "string" ? (s7.signature.startsWith("data:") ? s7.signature : `data:image/png;base64,${s7.signature}`) : null;
    autoTable(doc, {
      startY: yPosition,
      head: [["Name of Supervisor/Manager", "Signed", "Position", "Date"]],
      body: [[
        s7.supervisorManagerName || "",
        { content: " ", styles: { minCellHeight: 20, fillColor: [255, 255, 255], textColor: [0, 0, 0] } },
        s7.position || "",
        safePdfDate(s7.date || "")
      ]],
      columnStyles: {
        0: { cellWidth: contentW * 0.38 },
        1: { cellWidth: contentW * 0.22, halign: "center" },
        2: { cellWidth: contentW * 0.2 },
        3: { cellWidth: contentW * 0.2 }
      },
      styles: { font: "helvetica", fontSize: 6.4, cellPadding: 1.0, valign: "middle", fillColor: [255, 255, 255], textColor: [0, 0, 0] },
      theme: "grid",
      tableWidth: contentW,
      margin: tableMargin,
      headStyles: { lineWidth: 0.2, fontStyle: "bold", fillColor: THEME_RGB, textColor: [255, 255, 255] },
      bodyStyles: { lineWidth: 0.2, textColor: [0, 0, 0] },
      didDrawCell: (data) => {
        if (data.section === "body" && data.column.index === 1 && sig7) {
          const pageNumber = doc.internal.getCurrentPageInfo().pageNumber;
          pendingSignaturePromises.push(addSignatureToPDF(sig7, data.cell.x + 3, data.cell.y + 3, 22, 12, pageNumber));
        }
      }
    });
    yPosition = doc.lastAutoTable.finalY + 2;

    await Promise.all(pendingSignaturePromises);
    applyFooterAllPages(doc);

    const pd = fd.permitDetails || {};
    const safePermit = String(pd.permitNo || "Permit").replace(/[\\/:"*?<>|]+/g, "-");
    const safeDate = String(formatDate(record.date || pd.date) || "form").replace(/[\\/:"*?<>|]+/g, "-");
    let fileName;
    if (shouldUploadToSharePoint && uploadConfig) {
      fileName = buildSharePointFileName([
        addedByName(record),
        pd.location,
        toDdMmYyyy(record.date || pd.date),
      ]);
    } else {
      fileName = `SF027C_${safePermit}_${safeDate}.pdf`;
    }

    if (shouldUploadToSharePoint && uploadConfig) {
      try {
        const key = `${record._id || ""}|${pd.location || ""}|${record.date || ""}`;
        setUploadingKey(key);
        const apiUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
        const dataUri = doc.output("datauristring");
        const uploadParams = {
          fileName,
          fileBase64: dataUri,
          phaseType: uploadConfig.phaseType,
          phaseNumber: uploadConfig.phaseNumber,
          network: uploadConfig.network,
          siteName: uploadConfig.siteName, uploadContext: "hseq-forms-tablets", uploadSubFolder: "SF-027-C Permit to Break Ground"
        };
        const res = await fetch(`${apiUrl}/api/sharepoint/upload`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
          body: JSON.stringify(uploadParams)
        });
        if (res.status !== 204 && !res.ok) {
          let msg = "SharePoint upload failed";
          try {
            const out = await res.json();
            msg = out?.message || msg;
          } catch {
            /* ignore */
          }
          throw new Error(msg);
        }
        toast.success("PDF uploaded to SharePoint successfully!");
        if (uploadConfig.onSuccess) uploadConfig.onSuccess();
      } catch (err) {
        toast.error(err?.message || "SharePoint upload failed");
        if (uploadConfig.onError) uploadConfig.onError(err);
      } finally {
        setUploadingKey(null);
      }
    } else {
      doc.save(fileName);
      toast.success("SF-027-C Permit to Break Ground PDF generated successfully!");
    }
  };

  return (
    <>
      <div style={{ background: "#fff", borderRadius: 20, boxShadow: "0px 4px 20px rgba(0,0,0,0.08)", padding: 24, marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 16 }}>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: "#1a202c", margin: 0 }}>SF-027-C Permit to Break Ground</h2>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {isAdminUser && (
              <button
                type="button"
                onClick={() => setShowImportModal(true)}
                style={{
                  background: "#8b1634",
                  color: "#fff",
                  border: "none",
                  borderRadius: "10px",
                  padding: "10px 14px",
                  fontWeight: "600",
                  fontSize: "13px",
                  cursor: "pointer",
                }}
              >
                Import File
              </button>
            )}
          <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
            <i className="fa fa-search" style={{ position: "absolute", left: 12, color: THEME, fontSize: 14, pointerEvents: "none", zIndex: 1 }} />
            <input type="text" placeholder="Type to Search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} style={{ padding: "10px 12px 10px 36px", borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 14, outline: "none", width: 250, boxSizing: "border-box" }} onFocus={(e) => (e.target.style.borderColor = THEME)} onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")} />
          </div>
          </div>
        </div>
        <div style={{ overflowX: "auto", borderRadius: 12, border: "1px solid #e2e8f0" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f7fafc", borderBottom: "1px solid #e2e8f0" }}>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Permit No</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Location</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Responsible Person / HSEQ Advisor</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Permit Date</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Added By</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Updated Date</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loadingRecords ? (
                <tr><td colSpan={7} style={{ textAlign: "center", padding: 40, color: THEME, fontSize: 16, fontWeight: 600 }}><i className="fa fa-spinner fa-spin" style={{ marginRight: 8 }} />Loading records...</td></tr>
              ) : paged.length === 0 ? (
                <tr><td colSpan={7} style={{ textAlign: "center", padding: 40, color: "#718096", fontSize: 16 }}>No records found.</td></tr>
              ) : (
                paged.map((r, idx) => {
                  const pd = getRawFormData(r).permitDetails || {};
                  const updatedDate = r.updatedAt || r.createdAt || r.date || pd.date;
                  return (
                    <tr key={r._id || idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{pd.permitNo || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{pd.location || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{pd.responsiblePersonHseqAdvisor || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{formatDate(r.date || pd.date)}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{getUserName(r)}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{formatDate(updatedDate)}</td>
                      <td style={{ padding: 16 }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          {canEditDelete && (
                            <button onClick={() => handleEdit(r)} title="Edit" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}><i className="fa fa-edit" style={{ color: "#22c55e", fontSize: 16 }} /></button>
                          )}
                          <button type="button" onClick={() => generatePDF(r)} title="Generate PDF" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}><i className="fa fa-file-pdf-o" style={{ color: "#dc2626", fontSize: 16 }} /></button>
                          {(() => {
                            const pd0 = getRawFormData(r).permitDetails || {};
                            const k = `${r._id || ""}|${pd0.location || ""}|${r.date || ""}`;
                            const busy = uploadingKey === k;
                            return (
                              <button
                                type="button"
                                onClick={() => {
                                  if (!busy) {
                                    setSharePointUploadRecord(r);
                                    setSharePointPhaseType("phase");
                                    setSharePointPhaseNumber("15");
                                    setSharePointNetwork("");
                                    setSharePointPhaseType("non-phase"); setPendingDirectUpload(true);
                                  }
                                }}
                                disabled={busy}
                                title={busy ? "Uploading..." : "Upload to SharePoint"}
                                style={{ background: "transparent", border: "none", cursor: busy ? "not-allowed" : "pointer", padding: 6, opacity: busy ? 0.6 : 1 }}
                              >
                                <i className={busy ? "fa fa-spinner fa-spin" : "fa fa-upload"} style={{ color: "#22c55e", fontSize: 16 }} />
                              </button>
                            );
                          })()}
                          {canEditDelete && (
                            <button onClick={() => handleDelete(r._id)} title="Delete" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}><i className="fa fa-trash" style={{ color: "#e53e3e", fontSize: 16 }} /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: 18, gap: 12 }}>
          <button className="horizon-table-btn" disabled={page === 1} onClick={() => setPage(page - 1)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: page === 1 ? "#f7fafc" : "#fff", color: page === 1 ? "#a0aec0" : THEME, cursor: page === 1 ? "not-allowed" : "pointer" }}>Prev</button>
          <span style={{ fontWeight: 700, color: THEME, fontSize: 16 }}>{page} / {totalPages}</span>
          <button className="horizon-table-btn" disabled={page === totalPages} onClick={() => setPage(page + 1)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: page === totalPages ? "#f7fafc" : "#fff", color: page === totalPages ? "#a0aec0" : THEME, cursor: page === totalPages ? "not-allowed" : "pointer" }}>Next</button>
        </div>
      </div>

      {showEditModal && editForm && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={{ maxWidth: 1200, margin: "0 auto" }}>
              <div className="modal-header" style={{ background: THEME, color: "#fff" }}>
                <h5 className="modal-title">Edit Permit to Break Ground</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowEditModal(false)} />
              </div>
              <div className="modal-body" style={{ maxHeight: "70vh", overflowY: "auto" }}>
                {renderSection("Permit Details", (
                  <div>
                    <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #d1d5db", borderRadius: 8, overflow: "hidden" }}>
                      <tbody>
                        <tr>
                          <td style={{ ...tableHeaderStyle, width: "200px" }}>Permit No</td>
                          <td style={tableCellStyle}><input type="text" className="form-control form-control-sm border-0" value={fd.permitDetails?.permitNo || ""} onChange={(e) => updateFormData("permitDetails", { ...fd.permitDetails, permitNo: e.target.value })} style={{ width: "100%", fontSize: 12 }} /></td>
                        </tr>
                        <tr>
                          <td style={tableHeaderStyle}>Location</td>
                          <td style={tableCellStyle}><input type="text" className="form-control form-control-sm border-0" value={fd.permitDetails?.location || ""} onChange={(e) => updateFormData("permitDetails", { ...fd.permitDetails, location: e.target.value })} style={{ width: "100%", fontSize: 12 }} /></td>
                        </tr>
                        <tr>
                          <td style={tableHeaderStyle}>Responsible Person / HSEQ Advisor</td>
                          <td style={tableCellStyle}><input type="text" className="form-control form-control-sm border-0" value={fd.permitDetails?.responsiblePersonHseqAdvisor || ""} onChange={(e) => updateFormData("permitDetails", { ...fd.permitDetails, responsiblePersonHseqAdvisor: e.target.value })} style={{ width: "100%", fontSize: 12 }} /></td>
                        </tr>
                        <tr>
                          <td style={tableHeaderStyle}>Date</td>
                          <td style={tableCellStyle}><DatePicker selected={safeDate(fd.permitDetails?.date)} onChange={(d) => updateFormData("permitDetails", { ...fd.permitDetails, date: d ? dateToLocalYMD(d) : "" })} dateFormat="dd-MM-yyyy" className="form-control form-control-sm border-0" style={{ width: "100%", fontSize: 12 }} placeholderText="Select date" /></td>
                        </tr>
                        <tr>
                          <td style={tableHeaderStyle}>Valid from (time)</td>
                          <td style={tableCellStyle}><input type="text" className="form-control form-control-sm border-0" placeholder="Valid from" value={fd.permitDetails?.validFrom || ""} onChange={(e) => updateFormData("permitDetails", { ...fd.permitDetails, validFrom: e.target.value })} style={{ width: "100%", fontSize: 12 }} /></td>
                        </tr>
                        <tr>
                          <td style={tableHeaderStyle}>Valid to (time)</td>
                          <td style={tableCellStyle}><input type="text" className="form-control form-control-sm border-0" placeholder="Valid to" value={fd.permitDetails?.validTo || ""} onChange={(e) => updateFormData("permitDetails", { ...fd.permitDetails, validTo: e.target.value })} style={{ width: "100%", fontSize: 12 }} /></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ))}
                {renderSection("(D) Desktop Search - (RP/HSEQ)", (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div><label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 4 }}>Name</label><input type="text" className="form-control form-control-sm" value={fd.desktopSearch_rpHseq?.name || ""} onChange={(e) => updateFormData("desktopSearch_rpHseq", { ...fd.desktopSearch_rpHseq, name: e.target.value })} style={{ borderColor: THEME, borderRadius: 8 }} /></div>
                    <div><label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 4 }}>Date</label><DatePicker selected={safeDate(fd.desktopSearch_rpHseq?.date)} onChange={(d) => updateFormData("desktopSearch_rpHseq", { ...fd.desktopSearch_rpHseq, date: d ? dateToLocalYMD(d) : "" })} dateFormat="dd-MM-yyyy" className="form-control form-control-sm" style={{ borderColor: THEME, borderRadius: 8 }} placeholderText="Select date" /></div>
                  </div>
                ))}
                {renderSection("(C) Site Reconnaissance - (RP/HSEQ)", (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div><label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 4 }}>Name</label><input type="text" className="form-control form-control-sm" value={fd.siteReconnaissance_rpHseq?.name || ""} onChange={(e) => updateFormData("siteReconnaissance_rpHseq", { ...fd.siteReconnaissance_rpHseq, name: e.target.value })} style={{ borderColor: THEME, borderRadius: 8 }} /></div>
                    <div><label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 4 }}>Date</label><DatePicker selected={safeDate(fd.siteReconnaissance_rpHseq?.date)} onChange={(d) => updateFormData("siteReconnaissance_rpHseq", { ...fd.siteReconnaissance_rpHseq, date: d ? dateToLocalYMD(d) : "" })} dateFormat="dd-MM-yyyy" className="form-control form-control-sm" style={{ borderColor: THEME, borderRadius: 8 }} placeholderText="Select date" /></div>
                  </div>
                ))}
                {renderSection("(B) Detection Survey - PICex", (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div><label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 4 }}>Name</label><input type="text" className="form-control form-control-sm" value={fd.detectionSurvey_picex?.name || ""} onChange={(e) => updateFormData("detectionSurvey_picex", { ...fd.detectionSurvey_picex, name: e.target.value })} style={{ borderColor: THEME, borderRadius: 8 }} /></div>
                    <div><label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 4 }}>Date</label><DatePicker selected={safeDate(fd.detectionSurvey_picex?.date)} onChange={(d) => updateFormData("detectionSurvey_picex", { ...fd.detectionSurvey_picex, date: d ? dateToLocalYMD(d) : "" })} dateFormat="dd-MM-yyyy" className="form-control form-control-sm" style={{ borderColor: THEME, borderRadius: 8 }} placeholderText="Select date" /></div>
                  </div>
                ))}
                {renderSection("(A) Verification Detection / Trial Hole - (RP/HSEQ)", (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div><label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 4 }}>Name</label><input type="text" className="form-control form-control-sm" value={fd.verificationDetectionTrialHole_rpHseq?.name || ""} onChange={(e) => updateFormData("verificationDetectionTrialHole_rpHseq", { ...fd.verificationDetectionTrialHole_rpHseq, name: e.target.value })} style={{ borderColor: THEME, borderRadius: 8 }} /></div>
                    <div><label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 4 }}>Date</label><DatePicker selected={safeDate(fd.verificationDetectionTrialHole_rpHseq?.date)} onChange={(d) => updateFormData("verificationDetectionTrialHole_rpHseq", { ...fd.verificationDetectionTrialHole_rpHseq, date: d ? dateToLocalYMD(d) : "" })} dateFormat="dd-MM-yyyy" className="form-control form-control-sm" style={{ borderColor: THEME, borderRadius: 8 }} placeholderText="Select date" /></div>
                  </div>
                ))}
                {renderSection("Person in Charge of Excavation (PICex) / Person conducting buried service detection", (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div><label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 4 }}>Name</label><input type="text" className="form-control form-control-sm" value={fd.personInChargeOfExcavation?.name || ""} onChange={(e) => updateFormData("personInChargeOfExcavation", { ...fd.personInChargeOfExcavation, name: e.target.value })} style={{ borderColor: THEME, borderRadius: 8 }} /></div>
                    <div><label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 4 }}>Date</label><DatePicker selected={safeDate(fd.personInChargeOfExcavation?.date)} onChange={(d) => updateFormData("personInChargeOfExcavation", { ...fd.personInChargeOfExcavation, date: d ? dateToLocalYMD(d) : "" })} dateFormat="dd-MM-yyyy" className="form-control form-control-sm" style={{ borderColor: THEME, borderRadius: 8 }} placeholderText="Select date" /></div>
                  </div>
                ))}
                {renderSection("Section 1 - Services (YES/NO)", (
                  <div>
                    <div style={{ background: "rgba(139,22,52,0.1)", border: "2px solid #8b1634", borderRadius: 8, padding: 12, marginBottom: 16, color: THEME, fontSize: 12, fontWeight: 600 }}>HOLD: If you answer NO to any of the following questions, WORK MUST NOT PROCEED. Escalate to your supervisor/manager so that he can identify and implement additional control measures.</div>
                    {SECTION1_QUESTIONS.map((q) => renderYesNo(q, fd.section1_questions?.[q], (v) => updateFormData("section1_questions", { ...(fd.section1_questions || {}), [q]: v })))}
                  </div>
                ))}
                {renderSection("Section 2 - Excavations (YES/NO)", (
                  <div>
                    {SECTION2_QUESTIONS.map((q) => renderYesNo(q, fd.section2_questions?.[q], (v) => updateFormData("section2_questions", { ...(fd.section2_questions || {}), [q]: v })))}
                  </div>
                ))}
                {renderSection("CAT and Genny", (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div><label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 4 }}>CAT Serial No.</label><input type="text" className="form-control form-control-sm" value={fd.catAndGenny?.catSerialNo || ""} onChange={(e) => updateFormData("catAndGenny", { ...fd.catAndGenny, catSerialNo: e.target.value })} style={{ borderColor: THEME, borderRadius: 8 }} /></div>
                    <div><label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 4 }}>CAT Calibration Date</label><DatePicker selected={safeDate(fd.catAndGenny?.catCalibrationDate)} onChange={(d) => updateFormData("catAndGenny", { ...fd.catAndGenny, catCalibrationDate: d ? dateToLocalYMD(d) : "" })} dateFormat="dd-MM-yyyy" className="form-control form-control-sm" style={{ borderColor: THEME, borderRadius: 8 }} placeholderText="Select date" /></div>
                    <div><label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 4 }}>Genny Serial No.</label><input type="text" className="form-control form-control-sm" value={fd.catAndGenny?.gennySerialNo || ""} onChange={(e) => updateFormData("catAndGenny", { ...fd.catAndGenny, gennySerialNo: e.target.value })} style={{ borderColor: THEME, borderRadius: 8 }} /></div>
                    <div><label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 4 }}>Genny Calibration Date</label><DatePicker selected={safeDate(fd.catAndGenny?.gennyCalibrationDate)} onChange={(d) => updateFormData("catAndGenny", { ...fd.catAndGenny, gennyCalibrationDate: d ? dateToLocalYMD(d) : "" })} dateFormat="dd-MM-yyyy" className="form-control form-control-sm" style={{ borderColor: THEME, borderRadius: 8 }} placeholderText="Select date" /></div>
                  </div>
                ))}
                {renderSection("Services present", (
                  <div>
                    <p style={{ fontSize: 12, marginBottom: 12 }}>Services known or indicated within 1.5m of the excavation area or which may affect the work (tick if present)</p>
                    <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #d1d5db", borderRadius: 8, overflow: "hidden" }}>
                      <tbody>
                        <tr>
                          <td style={tableHeaderStyle}>Electricity</td>
                          <td style={tableHeaderStyle}>Low Voltage</td>
                          <td style={tableHeaderStyle}>Gas</td>
                          <td style={tableHeaderStyle}>Low Pressure</td>
                        </tr>
                        <tr>
                          <td style={tableCellStyle} />
                          <td style={{ ...tableCellStyle, textAlign: "center" }}><input type="checkbox" className="form-check-input theme-checkbox" checked={!!fd.servicesPresent?.electricity_lowVoltage} onChange={(e) => updateFormData("servicesPresent", { ...fd.servicesPresent, electricity_lowVoltage: e.target.checked })} style={{ width: 16, height: 16, accentColor: THEME }} /></td>
                          <td style={tableCellStyle} />
                          <td style={{ ...tableCellStyle, textAlign: "center" }}><input type="checkbox" className="form-check-input theme-checkbox" checked={!!fd.servicesPresent?.gas_lowPressure} onChange={(e) => updateFormData("servicesPresent", { ...fd.servicesPresent, gas_lowPressure: e.target.checked })} style={{ width: 16, height: 16, accentColor: THEME }} /></td>
                        </tr>
                        <tr>
                          <td style={tableCellStyle} />
                          <td style={tableCellStyle}>Overhead</td>
                          <td style={tableCellStyle} />
                          <td style={tableCellStyle} />
                        </tr>
                        <tr>
                          <td style={tableCellStyle} />
                          <td style={{ ...tableCellStyle, textAlign: "center" }}><input type="checkbox" className="form-check-input theme-checkbox" checked={!!fd.servicesPresent?.electricity_overhead} onChange={(e) => updateFormData("servicesPresent", { ...fd.servicesPresent, electricity_overhead: e.target.checked })} style={{ width: 16, height: 16, accentColor: THEME }} /></td>
                          <td style={tableCellStyle} />
                          <td style={tableCellStyle} />
                        </tr>
                        <tr>
                          <td style={tableHeaderStyle}>Water</td>
                          <td style={tableHeaderStyle}>Main less than 300mm dia.</td>
                          <td style={tableHeaderStyle}>Other</td>
                          <td style={tableHeaderStyle}>Sewer/drainage</td>
                        </tr>
                        <tr>
                          <td style={tableCellStyle} />
                          <td style={{ ...tableCellStyle, textAlign: "center" }}><input type="checkbox" className="form-check-input theme-checkbox" checked={!!fd.servicesPresent?.water_mainLess300mmDia} onChange={(e) => updateFormData("servicesPresent", { ...fd.servicesPresent, water_mainLess300mmDia: e.target.checked })} style={{ width: 16, height: 16, accentColor: THEME }} /></td>
                          <td style={tableCellStyle} />
                          <td style={{ ...tableCellStyle, textAlign: "center" }}><input type="checkbox" className="form-check-input theme-checkbox" checked={!!fd.servicesPresent?.other_sewerDrainage} onChange={(e) => updateFormData("servicesPresent", { ...fd.servicesPresent, other_sewerDrainage: e.target.checked })} style={{ width: 16, height: 16, accentColor: THEME }} /></td>
                        </tr>
                        <tr>
                          <td style={tableCellStyle} />
                          <td style={tableCellStyle}>Service</td>
                          <td style={tableCellStyle} />
                          <td style={tableCellStyle}>Communication</td>
                        </tr>
                        <tr>
                          <td style={tableCellStyle} />
                          <td style={{ ...tableCellStyle, textAlign: "center" }}><input type="checkbox" className="form-check-input theme-checkbox" checked={!!fd.servicesPresent?.service} onChange={(e) => updateFormData("servicesPresent", { ...fd.servicesPresent, service: e.target.checked })} style={{ width: 16, height: 16, accentColor: THEME }} /></td>
                          <td style={tableCellStyle} />
                          <td style={{ ...tableCellStyle, textAlign: "center" }}><input type="checkbox" className="form-check-input theme-checkbox" checked={!!fd.servicesPresent?.other_communication} onChange={(e) => updateFormData("servicesPresent", { ...fd.servicesPresent, other_communication: e.target.checked })} style={{ width: 16, height: 16, accentColor: THEME }} /></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ))}
                {renderSection("Hazardous services", (
                  <div>
                    <div style={{ background: "rgba(139,22,52,0.08)", border: "1px solid #8b1634", borderRadius: 8, padding: 12, marginBottom: 12, color: THEME, fontSize: 12, fontWeight: 600 }}>If any of the following services are indicated as present, DO NOT commence work until Section 4 of this permit has been completed by the Responsible Person / HSEQ Advisor.</div>
                    <table style={{ width: "100%", borderCollapse: "collapse", border: "1px solid #d1d5db", borderRadius: 8, overflow: "hidden" }}>
                      <tbody>
                        <tr>
                          <td style={tableHeaderStyle}>Electricity</td>
                          <td style={tableHeaderStyle}>Extra high voltage</td>
                          <td style={tableHeaderStyle}>Gas</td>
                          <td style={tableHeaderStyle}>High Pressure</td>
                        </tr>
                        <tr>
                          <td style={tableCellStyle} />
                          <td style={{ ...tableCellStyle, textAlign: "center" }}><input type="checkbox" className="form-check-input theme-checkbox" checked={!!fd.hazardousServices?.electricity_extraHighVoltage} onChange={(e) => updateFormData("hazardousServices", { ...fd.hazardousServices, electricity_extraHighVoltage: e.target.checked })} style={{ width: 16, height: 16, accentColor: THEME }} /></td>
                          <td style={tableCellStyle} />
                          <td style={{ ...tableCellStyle, textAlign: "center" }}><input type="checkbox" className="form-check-input theme-checkbox" checked={!!fd.hazardousServices?.gas_highPressure} onChange={(e) => updateFormData("hazardousServices", { ...fd.hazardousServices, gas_highPressure: e.target.checked })} style={{ width: 16, height: 16, accentColor: THEME }} /></td>
                        </tr>
                        <tr>
                          <td style={tableCellStyle} />
                          <td style={tableCellStyle}>High voltage (inc. overhead)</td>
                          <td style={tableCellStyle} />
                          <td style={tableCellStyle}>Medium/intermediate pressure</td>
                        </tr>
                        <tr>
                          <td style={tableCellStyle} />
                          <td style={{ ...tableCellStyle, textAlign: "center" }}><input type="checkbox" className="form-check-input theme-checkbox" checked={!!fd.hazardousServices?.electricity_highVoltageOverhead} onChange={(e) => updateFormData("hazardousServices", { ...fd.hazardousServices, electricity_highVoltageOverhead: e.target.checked })} style={{ width: 16, height: 16, accentColor: THEME }} /></td>
                          <td style={tableCellStyle} />
                          <td style={{ ...tableCellStyle, textAlign: "center" }}><input type="checkbox" className="form-check-input theme-checkbox" checked={!!fd.hazardousServices?.gas_mediumIntermediatePressure} onChange={(e) => updateFormData("hazardousServices", { ...fd.hazardousServices, gas_mediumIntermediatePressure: e.target.checked })} style={{ width: 16, height: 16, accentColor: THEME }} /></td>
                        </tr>
                        <tr>
                          <td style={tableHeaderStyle}>Water</td>
                          <td style={tableHeaderStyle}>Main (300mm dia. or over)</td>
                          <td style={tableHeaderStyle}>Other</td>
                          <td style={tableHeaderStyle}>Oil or Fuel</td>
                        </tr>
                        <tr>
                          <td style={tableCellStyle} />
                          <td style={{ ...tableCellStyle, textAlign: "center" }}><input type="checkbox" className="form-check-input theme-checkbox" checked={!!fd.hazardousServices?.water_main300mmOrOver} onChange={(e) => updateFormData("hazardousServices", { ...fd.hazardousServices, water_main300mmOrOver: e.target.checked })} style={{ width: 16, height: 16, accentColor: THEME }} /></td>
                          <td style={tableCellStyle} />
                          <td style={{ ...tableCellStyle, textAlign: "center" }}><input type="checkbox" className="form-check-input theme-checkbox" checked={!!fd.hazardousServices?.water_oilOrFuel} onChange={(e) => updateFormData("hazardousServices", { ...fd.hazardousServices, water_oilOrFuel: e.target.checked })} style={{ width: 16, height: 16, accentColor: THEME }} /></td>
                        </tr>
                        <tr>
                          <td style={tableCellStyle} />
                          <td style={tableCellStyle}>GRP or UPVC main (requires isolation before excavating)</td>
                          <td style={tableCellStyle} />
                          <td style={tableCellStyle}>Thrust blocks</td>
                        </tr>
                        <tr>
                          <td style={tableCellStyle} />
                          <td style={{ ...tableCellStyle, textAlign: "center" }}><input type="checkbox" className="form-check-input theme-checkbox" checked={!!fd.hazardousServices?.water_grpOrUpvcMain} onChange={(e) => updateFormData("hazardousServices", { ...fd.hazardousServices, water_grpOrUpvcMain: e.target.checked })} style={{ width: 16, height: 16, accentColor: THEME }} /></td>
                          <td style={tableCellStyle} />
                          <td style={{ ...tableCellStyle, textAlign: "center" }}><input type="checkbox" className="form-check-input theme-checkbox" checked={!!fd.hazardousServices?.other_thrustBlocks} onChange={(e) => updateFormData("hazardousServices", { ...fd.hazardousServices, other_thrustBlocks: e.target.checked })} style={{ width: 16, height: 16, accentColor: THEME }} /></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ))}
                {renderSection("Section 3 - Issue/authorisation (all answers YES)", (
                  <div>
                    <p style={{ fontSize: 12, marginBottom: 10 }}>I confirm that work is safe to proceed in accordance with the risk assessments and controls identified above</p>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 10 }}>
                      <div><label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 6 }}>Position</label><input type="text" className="form-control form-control-sm" value={fd.section3_issueAuthorisation?.position || ""} onChange={(e) => updateFormData("section3_issueAuthorisation", { ...fd.section3_issueAuthorisation, position: e.target.value })} style={{ borderColor: THEME, borderRadius: 8 }} /></div>
                      <div><label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 6 }}>Date</label><DatePicker selected={safeDate(fd.section3_issueAuthorisation?.date)} onChange={(d) => updateFormData("section3_issueAuthorisation", { ...fd.section3_issueAuthorisation, date: d ? dateToLocalYMD(d) : "" })} dateFormat="dd-MM-yyyy" className="form-control form-control-sm" style={{ borderColor: THEME, borderRadius: 8 }} placeholderText="Select date" /></div>
                    </div>
                    <div><label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 6 }}>Signed</label>{renderSignature("s3", fd.section3_issueAuthorisation?.signature)}</div>
                  </div>
                ))}
                {renderSection("Section 4 - Issue/authorisation (one or more NO)", (
                  <div>
                    <p style={{ fontSize: 12, marginBottom: 10 }}>The following action has been taken/control measures put in place to ensure that work can proceed safely</p>
                    {renderYesNo("Responsible Person / HSEQ Advisor mitigation", fd.section4_issueAuthorisation?.responsiblePersonMitigation, (v) => updateFormData("section4_issueAuthorisation", { ...fd.section4_issueAuthorisation, responsiblePersonMitigation: v }))}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 10 }}>
                      <div><label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 6 }}>Position</label><input type="text" className="form-control form-control-sm" value={fd.section4_issueAuthorisation?.position || ""} onChange={(e) => updateFormData("section4_issueAuthorisation", { ...fd.section4_issueAuthorisation, position: e.target.value })} style={{ borderColor: THEME, borderRadius: 8 }} /></div>
                      <div><label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 6 }}>Date</label><DatePicker selected={safeDate(fd.section4_issueAuthorisation?.date)} onChange={(d) => updateFormData("section4_issueAuthorisation", { ...fd.section4_issueAuthorisation, date: d ? dateToLocalYMD(d) : "" })} dateFormat="dd-MM-yyyy" className="form-control form-control-sm" style={{ borderColor: THEME, borderRadius: 8 }} placeholderText="Select date" /></div>
                    </div>
                  </div>
                ))}
                {renderSection("Type (A) Survey", (
                  <div>
                    <p style={{ fontSize: 12, marginBottom: 10 }}>Type (A) Survey. Verification Detection has been undertaken. This requires a trial hole / slit to be excavated to a width of xx metre and depth of XX metres or to a width or depth that is safe and practicable to do so.</p>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 10 }}>
                      <div><label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 6 }}>Width (metres)</label><input type="text" className="form-control form-control-sm" value={fd.typeASurvey?.width || ""} onChange={(e) => updateFormData("typeASurvey", { ...fd.typeASurvey, width: e.target.value })} style={{ borderColor: THEME, borderRadius: 8 }} /></div>
                      <div><label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 6 }}>Depth (metres)</label><input type="text" className="form-control form-control-sm" value={fd.typeASurvey?.depth || ""} onChange={(e) => updateFormData("typeASurvey", { ...fd.typeASurvey, depth: e.target.value })} style={{ borderColor: THEME, borderRadius: 8 }} /></div>
                    </div>
                    <div style={{ marginBottom: 10 }}><label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 6 }}>Name of Responsible Person / HSEQ Advisor</label><input type="text" className="form-control form-control-sm" value={fd.typeASurvey?.responsiblePersonName || ""} onChange={(e) => updateFormData("typeASurvey", { ...fd.typeASurvey, responsiblePersonName: e.target.value })} style={{ borderColor: THEME, borderRadius: 8 }} /></div>
                    <div><label style={{ fontSize: 12, color: THEME }}>Signed</label>{renderSignature("ta", fd.typeASurvey?.signature)}</div>
                  </div>
                ))}
                {renderSection("Section 5 - PICex Acceptance", (
                  <div>
                    {["I understand the validity and conditions of authorisation placed on this permit by The Responsible Person / HSEQ Advisor.", "I agree to carry out the work in accordance with those conditions of authorisation and the associated Sae System Of Work.", "I have conducted any surveys required of the conditions of the permit to breaking ground.", "I have briefed all the members of the work group on the risk controls to be applied for conducting the work safely."].map((q) => (
                      <div key={q} style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                        <input type="checkbox" className="form-check-input theme-checkbox" checked={!!fd.section5_picexAcceptance?.[q]} onChange={(e) => updateFormData("section5_picexAcceptance", { ...fd.section5_picexAcceptance, [q]: e.target.checked })} style={{ width: 16, height: 16, accentColor: THEME }} />
                        <span style={{ fontSize: 12 }}>{q}</span>
                      </div>
                    ))}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginTop: 10 }}>
                      <div><label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 6 }}>Name</label><input type="text" className="form-control form-control-sm" value={fd.section5_picexAcceptance?.name || ""} onChange={(e) => updateFormData("section5_picexAcceptance", { ...fd.section5_picexAcceptance, name: e.target.value })} style={{ borderColor: THEME, borderRadius: 8 }} /></div>
                      <div><label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 6 }}>Date</label><DatePicker selected={safeDate(fd.section5_picexAcceptance?.date)} onChange={(d) => updateFormData("section5_picexAcceptance", { ...fd.section5_picexAcceptance, date: d ? dateToLocalYMD(d) : "" })} dateFormat="dd-MM-yyyy" className="form-control form-control-sm" style={{ borderColor: THEME, borderRadius: 8 }} placeholderText="Select date" /></div>
                      <div><label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 6 }}>Time</label><input type="time" className="form-control form-control-sm" value={fd.section5_picexAcceptance?.time || ""} onChange={(e) => updateFormData("section5_picexAcceptance", { ...fd.section5_picexAcceptance, time: e.target.value })} style={{ borderColor: THEME, borderRadius: 8 }} /></div>
                    </div>
                    <div style={{ marginTop: 10 }}><label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 6 }}>Signed</label>{renderSignature("s5", fd.section5_picexAcceptance?.signature)}</div>
                  </div>
                ))}
                {renderSection("Briefed to", (
                  <div>
                    <p style={{ fontSize: 12, marginBottom: 10 }}>By signing below I confirm I have received and understood the briefing for this task</p>
                    <div style={{ marginBottom: 10 }}><label style={{ fontSize: 12, color: THEME }}>Name</label><input type="text" className="form-control form-control-sm" value={fd.briefedTo?.name || ""} onChange={(e) => updateFormData("briefedTo", { ...fd.briefedTo, name: e.target.value })} style={{ borderColor: THEME, borderRadius: 8 }} /></div>
                    <div style={{ marginBottom: 10 }}><label style={{ fontSize: 12, color: THEME }}>Signed</label>{renderSignature("bt", fd.briefedTo?.signature)}</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                      <div><label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 6 }}>Date</label><DatePicker selected={safeDate(fd.briefedTo?.date)} onChange={(d) => updateFormData("briefedTo", { ...fd.briefedTo, date: d ? dateToLocalYMD(d) : "" })} dateFormat="dd-MM-yyyy" className="form-control form-control-sm" style={{ borderColor: THEME, borderRadius: 8 }} placeholderText="Select date" /></div>
                      <div><label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 6 }}>Time</label><input type="time" className="form-control form-control-sm" value={fd.briefedTo?.time || ""} onChange={(e) => updateFormData("briefedTo", { ...fd.briefedTo, time: e.target.value })} style={{ borderColor: THEME, borderRadius: 8 }} /></div>
                    </div>
                  </div>
                ))}
                {renderSection("Section 6 - Cancellation", (
                  <div>
                    <p style={{ fontSize: 12, marginBottom: 10 }}>I confirm that work has been completed / not completed and a new permit has been issued (delete as appropriate)</p>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                      <div><label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 6 }}>Signed</label>{renderSignature("s6", fd.section6_cancellation?.signature)}</div>
                      <div><label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 6 }}>Position</label><input type="text" className="form-control form-control-sm" value={fd.section6_cancellation?.position || ""} onChange={(e) => updateFormData("section6_cancellation", { ...fd.section6_cancellation, position: e.target.value })} style={{ borderColor: THEME, borderRadius: 8 }} /></div>
                      <div><label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 6 }}>Date</label><DatePicker selected={safeDate(fd.section6_cancellation?.date)} onChange={(d) => updateFormData("section6_cancellation", { ...fd.section6_cancellation, date: d ? dateToLocalYMD(d) : "" })} dateFormat="dd-MM-yyyy" className="form-control form-control-sm" style={{ borderColor: THEME, borderRadius: 8 }} placeholderText="Select date" /></div>
                    </div>
                  </div>
                ))}
                {renderSection("Section 7 - Responsible Person / HSEQ Advisor sketch of proposed location", (
                  <div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                      <div><label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 6 }}>Sketch</label><div style={{ border: "2px solid #e2e8f0", borderRadius: 12, padding: 16, minHeight: 120, background: "#fafafa", display: "flex", alignItems: "center", justifyContent: "center" }}>{fd.section7_sketch?.sketchImage ? <img src={fd.section7_sketch.sketchImage.startsWith("data:image") ? fd.section7_sketch.sketchImage : fd.section7_sketch.sketchImage.startsWith("http") ? fd.section7_sketch.sketchImage : `data:image/png;base64,${fd.section7_sketch.sketchImage}`} alt="Sketch" style={{ maxWidth: 320, maxHeight: 140, objectFit: "contain", borderRadius: 8 }} onError={(e) => { e.target.style.display = "none"; }} /> : null}{!fd.section7_sketch?.sketchImage ? <div style={{ color: "#94a3b8", fontSize: 13 }}>No sketch</div> : null}</div></div>
                      <div><label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 6 }}>Name of Supervisor/Manager</label><input type="text" className="form-control form-control-sm" value={fd.section7_sketch?.supervisorManagerName || ""} onChange={(e) => updateFormData("section7_sketch", { ...fd.section7_sketch, supervisorManagerName: e.target.value })} style={{ borderColor: THEME, borderRadius: 8 }} /></div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                      <div><label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 6 }}>Signed</label>{renderSignature("s7", fd.section7_sketch?.signature)}</div>
                      <div><label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 6 }}>Position</label><input type="text" className="form-control form-control-sm" value={fd.section7_sketch?.position || ""} onChange={(e) => updateFormData("section7_sketch", { ...fd.section7_sketch, position: e.target.value })} style={{ borderColor: THEME, borderRadius: 8 }} /></div>
                      <div><label style={{ fontSize: 12, color: THEME, display: "block", marginBottom: 6 }}>Date</label><DatePicker selected={safeDate(fd.section7_sketch?.date)} onChange={(d) => updateFormData("section7_sketch", { ...fd.section7_sketch, date: d ? dateToLocalYMD(d) : "" })} dateFormat="dd-MM-yyyy" className="form-control form-control-sm" style={{ borderColor: THEME, borderRadius: 8 }} placeholderText="Select date" /></div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
                <button type="button" className="btn" style={{ background: THEME, color: "#fff", fontWeight: 600 }} onClick={handleEditSave}>Submit</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showSharePointUploadModal && sharePointUploadRecord && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ maxWidth: 600, margin: "0 auto" }}>
              <div className="modal-header">
                <h5 className="modal-title">Upload to SharePoint</h5>
                <button type="button" className="btn-close" onClick={() => { setShowSharePointUploadModal(false); setSharePointUploadRecord(null); setSharePointPhaseType("phase"); setSharePointPhaseNumber("15"); setSharePointNetwork(""); }} />
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="mb-3 col-12">
                    <label className="form-label fw-semibold">Type <span style={{ color: "red" }}>*</span></label>
                    <select className="form-select" value={sharePointPhaseType} onChange={(e) => { setSharePointPhaseType(e.target.value); if (e.target.value === "non-phase") setSharePointNetwork(""); }}>
                      <option value="phase">Phase</option>
                      <option value="non-phase">Non Phase</option>
                    </select>
                  </div>
                  {sharePointPhaseType === "phase" && (
                    <>
                      <div className="mb-3 col-md-6">
                        <label className="form-label fw-semibold">Phase Number <span style={{ color: "red" }}>*</span></label>
                        <select className="form-select" value={sharePointPhaseNumber} onChange={(e) => setSharePointPhaseNumber(e.target.value)}>
                          <option value="15">Phase 15 (2025)</option>
                          <option value="16">Phase 16 (2026)</option>
                        </select>
                      </div>
                      <div className="mb-3 col-md-6">
                        <label className="form-label fw-semibold">Area <span style={{ color: "red" }}>*</span></label>
                        <select className="form-select" value={sharePointNetwork} onChange={(e) => setSharePointNetwork(e.target.value)}>
                          <option value="">Select Area</option>
                          <option value="EPN">EPN</option>
                          <option value="LPN">LPN</option>
                          <option value="SPN">SPN</option>
                        </select>
                      </div>
                    </>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => { setShowSharePointUploadModal(false); setSharePointUploadRecord(null); setSharePointPhaseType("phase"); setSharePointPhaseNumber("15"); setSharePointNetwork(""); }}>Cancel</button>
                <button type="button" className="btn" style={{ background: THEME, color: "#fff", fontWeight: 600 }} onClick={handleSharePointUpload}>Upload to SharePoint</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AdminTaylorImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Upload SF-027-C Permit to Break Ground File"
        importPath="permit-to-break-ground/import-form"
        isAdminUser={isAdminUser}
        onImported={({ insertedRow }) => {
          if (insertedRow && insertedRow._id) {
            setForms((prev) => [insertedRow, ...prev]);
            setPage(1);
          }
        }}
      />

      {showDeleteModal && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header"><h5 className="modal-title">Delete Permit to Break Ground</h5><button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)} /></div>
              <div className="modal-body">Are you sure you want to delete this form?</div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                <button type="button" className="btn btn-danger" onClick={confirmDelete}>Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default PermitToBreakGroundPage;
